<template>
    <div class="view-switcher">
        <div class="view-item title-item" @click="selectMainTable">
            <i class="material-icons">table_chart</i>
            {{ $t("tables.mainTable") }} - {{ table.name }}
        </div>
        <a-divider />
        <div class="views-list-container">
            <div class="view-item" v-for="view in table.views" :key="view._id">
                <div class="view-content" @click="selectView(view)">
                    <i class="material-icons">keyboard_tab</i>
                    {{ view.name }}
                </div>
                <div class="view-actions" @click.stop="$_.noop">
                    <i class="material-icons edit-icon" @click.stop="editView(view)">edit</i>
                    <a-dropdown :trigger="['click']">
                        <i class="material-icons more-icon" @click.stop>more_vert</i>
                        <a-menu slot="overlay" class="view-actions-menu">
                            <a-menu-item @click="renameView(view)">
                                <i class="material-icons">edit</i>
                                {{ $t("tables.renameView") }}
                            </a-menu-item>
                            <a-menu-item @click="duplicateView(view)">
                                <i class="material-icons">content_copy</i>
                                {{ $t("tables.duplicateView") }}
                            </a-menu-item>
                            <a-menu-item @click="confirmDeleteView(view)">
                                <i class="material-icons" style="color: #ff4d4f">delete</i>
                                {{ $t("tables.deleteView") }}
                            </a-menu-item>
                        </a-menu>
                    </a-dropdown>
                </div>
            </div>
        </div>

        <a-divider />

        <div class="view-item other-tables" @click="showOtherTables">
            <div class="view-content">
                <i class="material-icons">view_list</i>
                {{ $t("tables.otherTables") }}
            </div>
            <div class="view-actions">
                <a-popover
                    placement="rightTop"
                    trigger="click"
                    v-model="otherTablesVisible"
                    overlayClassName="other-tables-popover"
                >
                    <template slot="content">
                        <div class="other-tables-menu">
                            <div class="other-tables-header">
                                <div class="title">{{ $t("tables.otherTables") }}</div>
                                <a-input-search
                                    v-model="tableSearchText"
                                    :placeholder="$t('message.search')"
                                    style="width: 100%; margin-bottom: 10px"
                                    @input="onTableSearch"
                                />
                            </div>
                            <div class="other-tables-list">
                                <div
                                    v-for="table in filteredTables"
                                    :key="table._id"
                                    class="other-table-item"
                                    @click="navigateToTable(table._id)"
                                >
                                    <i class="material-icons">table_chart</i>
                                    <span>{{ table.name }}</span>
                                </div>
                                <div v-if="filteredTables.length === 0" class="no-tables">
                                    {{ $t("tables.noTablesFound") }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <i class="material-icons" @click.stop>keyboard_arrow_right</i>
                </a-popover>
            </div>
        </div>
        <a-divider />
        <PermissionController :permissions="['table.update']">
            <div class="view-item add-new" @click="createNewView">
                <i class="material-icons">add</i>
                {{ $t("tables.newView") }}
            </div>
        </PermissionController>
        <a-divider />
        <div class="footer-text">
            {{ $t("tables.createViewaFiltering") }}
        </div>

        <!-- Rename View Modal -->
        <Modal2
            v-if="showRenameModal"
            :title="$t('tables.renameView')"
            @cancel="closeRenameModal"
            @ok="handleRename"
            :okText="$t('message.save')"
            :cancelText="$t('message.cancel')"
        >
            <a-form-item :label="$t('tables.enterNewViewName')">
                <a-input v-model="newViewName" placeholder="Görünüm adı girin" />
            </a-form-item>
        </Modal2>
    </div>
</template>

<script>
import { TABLE_API } from "../../../../../api/table";
import Modal2 from "../../../../../components/common/Modal2.vue";
import { mapState, mapActions } from "vuex";

export default {
    name: "ViewSwitcher",
    components: {
        Modal2,
    },
    props: {
        views: {
            type: Array,
            default: () => [],
        },
        table: {
            type: Object,
            required: true,
        },
        selectedViewId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showRenameModal: false,
            newViewName: "",
            selectedView: null,
            otherTablesVisible: false,
            tableSearchText: "",
        };
    },
    computed: {
        ...mapState({
            allTables: (state) => state.table.tables,
        }),
        filteredTables() {
            if (!this.tableSearchText) {
                return this.allTables.filter((t) => t._id !== this.table._id);
            }
            const searchLower = this.tableSearchText.toLowerCase();
            return this.allTables
                .filter((t) => t._id !== this.table._id)
                .filter(
                    (t) =>
                        t.name.toLowerCase().includes(searchLower) ||
                        (t.description && t.description.toLowerCase().includes(searchLower))
                );
        },
    },
    methods: {
        ...mapActions({
            fetchCustomerTables: "table/fetchCustomerTables",
        }),
        selectMainTable() {
            this.$emit("show-view", null);
        },
        selectView(view) {
            this.$emit("show-view", view);
        },
        editView(view) {
            this.$emit("edit-view", view);
        },
        createNewView() {
            this.$emit("create-new-view");
        },
        duplicateView(view) {
            this.$confirm({
                title: this.$t("tables.duplicateView"),
                content: this.$t("tables.confirmDuplicateView"),
                okText: this.$t("message.yes"),
                cancelText: this.$t("message.no"),
                onOk: async () => {
                    try {
                        // Görünümü çoğaltmak için gerekli API çağrısı
                        const duplicatedView = await TABLE_API.createView(
                            this.table._id,
                            `${view.name} (Kopya)`,
                            view.description,
                            view.filters,
                            view.sort,
                            view.cursor,
                            view.search,
                            view.hiddenFields,
                            view.lockedFields,
                            view.groupValue,
                            view.colorConditions
                        );

                        this.$emit("view-duplicated", duplicatedView);
                        this.$message.success(this.$t("tables.viewDuplicatedSuccess"));
                        this.selectView(duplicatedView);
                    } catch (error) {
                        console.error("Görünüm çoğaltılırken hata oluştu:", error);
                        this.$message.error(this.$t("tables.viewDuplicateError"));
                    }
                },
            });
        },
        confirmDeleteView(view) {
            this.$confirm({
                title: this.$t("tables.deleteView"),
                content: this.$t("tables.confirmDeleteView"),
                okText: this.$t("message.yes"),
                okType: "danger",
                cancelText: this.$t("message.no"),
                onOk: async () => {
                    try {
                        // Görünümü silmek için gerekli API çağrısı
                        await TABLE_API.deleteView(this.table._id, view._id);
                        this.selectMainTable();
                        this.$emit("view-deleted", view._id);
                        this.$message.success(this.$t("tables.viewDeletedSuccess"));
                        if (this.selectedViewId === view._id) {
                            this.$emit("show-view", null);
                        }
                    } catch (error) {
                        console.error("Görünüm silinirken hata oluştu:", error);
                        this.$message.error(this.$t("tables.viewDeleteError"));
                    }
                },
            });
        },
        renameView(view) {
            this.selectedView = view;
            this.newViewName = view.name;
            this.showRenameModal = true;
        },
        closeRenameModal() {
            this.showRenameModal = false;
            this.selectedView = null;
        },
        async handleRename() {
            if (!this.newViewName.trim()) {
                this.$message.warning(this.$t("tables.viewNameRequired"));
                return;
            }

            try {
                // Görünümü yeniden adlandırmak için API çağrısı
                const updatedView = await TABLE_API.updateView(this.table._id, this.selectedView._id, {
                    name: this.newViewName.trim(),
                });

                // Güncellenmiş görünümü emit et
                this.$emit("view-updated", updatedView);
                this.$message.success(this.$t("tables.viewRenamedSuccess"));
                this.closeRenameModal();
            } catch (error) {
                console.error("Görünüm yeniden adlandırılırken hata oluştu:", error);
                this.$message.error(this.$t("tables.viewRenameError"));
            }
        },
        showOtherTables(event) {
            if (event) {
                event.stopPropagation();
            }
            this.otherTablesVisible = !this.otherTablesVisible;
        },
        onTableSearch() {
            // Arama yapıldığında filtreleme otomatik olarak computed property ile yapılıyor
        },
        navigateToTable(tableId) {
            this.otherTablesVisible = false;
            this.$router.push(`/tables`);
            setTimeout(() => {
                this.$router.push(`/tables/t/${tableId}`);
            }, 100);
        },
    },
    created() {
        // Tabloları yükle
        this.fetchCustomerTables();
    },
};
</script>

<style scoped lang="scss">
.view-switcher {
    width: 500px;

    .ant-divider {
        margin: 5px 0;
    }

    .views-list-container {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;

        /* Scrollbar stilleri */
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 3px;

            &:hover {
                background: #555;
            }
        }
    }

    .view-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
        color: #22272d;
        cursor: pointer;

        &.title-item {
            font-weight: bold;
            font-size: 16px;
            color: #22272d;
        }

        &:hover {
            background-color: #f5f5f5;
        }

        .view-content {
            display: flex;
            align-items: center;
            flex-grow: 1;

            .material-icons {
                margin-right: 8px;
            }
        }

        .view-actions {
            display: flex;
            align-items: center;

            .material-icons {
                font-size: 18px;
                color: #1890ff;
                margin-left: 4px;

                &:hover {
                    color: #40a9ff;
                }

                &.more-icon {
                    color: #999;

                    &:hover {
                        color: #666;
                    }
                }
            }
        }

        &.add-new {
            color: #1890ff;

            .material-icons {
                color: #1890ff;
                margin-right: 8px;
            }
        }
    }

    .footer-text {
        padding: 8px 12px;
        font-size: 12px;
        color: #999;
        background-color: #f5f5f5;
    }
}
</style>
<style lang="scss">
.view-actions-menu {
    .ant-dropdown-menu-item {
        padding: 5px;
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        align-items: center;
    }
}

.other-tables-popover {
    .ant-popover-inner-content {
        padding: 0;
    }

    .other-tables-menu {
        width: 300px;
        max-height: 400px;
        display: flex;
        flex-direction: column;

        .other-tables-header {
            padding: 12px;
            border-bottom: 1px solid #e8e8e8;

            .title {
                font-weight: bold;
                margin-bottom: 10px;
                font-size: 16px;
            }
        }

        .other-tables-list {
            max-height: 300px;
            overflow-y: auto;

            .other-table-item {
                display: flex;
                align-items: center;
                padding: 8px 12px;
                cursor: pointer;

                &:hover {
                    background-color: #f5f5f5;
                }

                i {
                    margin-right: 8px;
                    color: #22272d;
                }
            }

            .no-tables {
                padding: 12px;
                text-align: center;
                color: #999;
            }
        }
    }
}
</style>
